import { View, Text, StyleSheet, Svg, Path, Circle, G } from "@react-pdf/renderer";
import type { ResultSegment } from "../../types";

const styles = StyleSheet.create({
  container: {
    marginVertical: 20,
    alignItems: "center"
  },
  title: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#374151",
    marginBottom: 10
  },
  chartContainer: {
    width: 350,
    height: 350,
    alignItems: "center",
    justifyContent: "center"
  },
  legend: {
    marginTop: 15,
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 10
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    marginHorizontal: 5
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 4
  },
  legendText: {
    fontSize: 8,
    color: "#6b7280"
  }
});

interface RadarChartProperties {
  resultados: ResultSegment[];
  showAutoAvaliacao?: boolean;
  primaryColor?: string;
  secondaryColor?: string;
}

/**
 * Generates the points for the radar chart polygon
 * @param values
 * @param centerX
 * @param centerY
 * @param radius
 */
function getRadarPoints(values: number[], centerX: number, centerY: number, radius: number): string {
  const angleStep = (2 * Math.PI) / values.length;
  const points: string[] = [];

  for (const [index, value] of values.entries()) {
    const angle = index * angleStep - Math.PI / 2; // Start from top
    const normalizedValue = value / 100;
    const x = centerX + radius * normalizedValue * Math.cos(angle);
    const y = centerY + radius * normalizedValue * Math.sin(angle);
    points.push(`${x.toFixed(2)},${y.toFixed(2)}`);
  }

  return `M ${points.join(" L ")} Z`;
}

/**
 * Generates grid lines for the radar background
 * @param centerX
 * @param centerY
 * @param radius
 * @param segments
 * @param levels
 */
function getGridPaths(centerX: number, centerY: number, radius: number, segments: number, levels: number): string[] {
  const paths: string[] = [];
  const angleStep = (2 * Math.PI) / segments;

  // Axis lines from center to edges
  for (let index = 0; index < segments; index++) {
    const angle = index * angleStep - Math.PI / 2;
    const x = centerX + radius * Math.cos(angle);
    const y = centerY + radius * Math.sin(angle);
    paths.push(`M ${centerX},${centerY} L ${x.toFixed(2)},${y.toFixed(2)}`);
  }

  // Concentric polygon levels
  for (let level = 1; level <= levels; level++) {
    const levelRadius = (radius * level) / levels;
    const levelPoints: string[] = [];
    for (let index = 0; index < segments; index++) {
      const angle = index * angleStep - Math.PI / 2;
      const x = centerX + levelRadius * Math.cos(angle);
      const y = centerY + levelRadius * Math.sin(angle);
      levelPoints.push(`${x.toFixed(2)},${y.toFixed(2)}`);
    }
    paths.push(`M ${levelPoints.join(" L ")} Z`);
  }

  return paths;
}

/**
 * RadarChart component for React-PDF
 *
 * Uses React-PDF's Svg primitives to render a radar chart directly in the PDF.
 * This avoids external image generation and keeps everything in the React tree.
 * @param root0
 * @param root0.resultados
 * @param root0.showAutoAvaliacao
 * @param root0.primaryColor
 * @param root0.secondaryColor
 */
export function RadarChart({
  resultados,
  showAutoAvaliacao = true,
  primaryColor = "#3b82f6",
  secondaryColor = "#93c5fd"
}: RadarChartProperties) {
  const width = 350;
  const height = 350;
  const centerX = width / 2;
  const centerY = height / 2;
  const radius = Math.min(width, height) / 2 - 60;

  const values = resultados.map((r) => r.value);
  const autoAvaliacaoValues = resultados.map((r) => r.autoAvaliacao ?? 0);
  const hasAutoAvaliacao = showAutoAvaliacao && resultados.some((r) => r.autoAvaliacao !== undefined);

  const gridPaths = getGridPaths(centerX, centerY, radius, resultados.length, 4);
  const dataPath = getRadarPoints(values, centerX, centerY, radius);
  const autoAvaliacaoPath = hasAutoAvaliacao ? getRadarPoints(autoAvaliacaoValues, centerX, centerY, radius) : "";

  const angleStep = (2 * Math.PI) / resultados.length;

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Resultados por Dimensão</Text>

      <View style={styles.chartContainer}>
        <Svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          {/* Grid lines */}
          <G stroke="#e5e7eb" strokeWidth={1} fill="none">
            {gridPaths.map((path, index) => (
              <Path key={`grid-${index}`} d={path} />
            ))}
          </G>

          {/* Auto-avaliação area (if present) */}
          {hasAutoAvaliacao && autoAvaliacaoPath && (
            <Path
              d={autoAvaliacaoPath}
              fill={secondaryColor}
              fillOpacity={0.3}
              stroke={secondaryColor}
              strokeWidth={2}
            />
          )}

          {/* Main data area */}
          <Path d={dataPath} fill={primaryColor} fillOpacity={0.3} stroke={primaryColor} strokeWidth={2} />

          {/* Data points */}
          <G fill={primaryColor}>
            {values.map((value, index) => {
              const angle = index * angleStep - Math.PI / 2;
              const normalizedValue = value / 100;
              const x = centerX + radius * normalizedValue * Math.cos(angle);
              const y = centerY + radius * normalizedValue * Math.sin(angle);
              return <Circle key={`point-${index}`} cx={x} cy={y} r={4} />;
            })}
          </G>
        </Svg>
      </View>

      {/* Legend */}
      <View style={styles.legend}>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: primaryColor }]} />
          <Text style={styles.legendText}>Pontuação da Empresa</Text>
        </View>
        {hasAutoAvaliacao && (
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: secondaryColor }]} />
            <Text style={styles.legendText}>Autoavaliação</Text>
          </View>
        )}
      </View>
    </View>
  );
}
